import { useCallback, useMemo } from 'react';
import type { UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import type { PersonaDto, ScenarioPersonaDto, PersonaUsage } from '@/api-gen/api.schemas';
import { PersonaUsage as PersonaUsageEnum } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/hooks/useScenarioForm';
import { usePersonaUsageValidation } from './usePersonaUsageValidation';

export interface UseScenarioPersonasResult {
  /** Currently selected default persona (may be undefined) */
  defaultPersona: ScenarioPersonaDto | undefined;
  /** Currently selected intro persona (may be undefined) */
  introPersona: ScenarioPersonaDto | undefined;
  /** True if there's a validation error with default persona (not exactly one) */
  hasDefaultPersonaError: boolean;
  /** Make the provided personaId the Default (or clear if null) */
  setDefaultPersona: (personaId: number | null) => void;
  /** Make the provided personaId the Intro persona (or clear if null) */
  setIntroPersona: (personaId: number | null) => void;
  /** Add a persona with given usages. Returns success + optional message */
  addPersona: (persona: PersonaDto, usages: PersonaUsage[], defaultRoleId?: number | null) => { success: boolean; message?: string };
  /** Remove persona by id */
  removePersona: (personaId: number) => void;
  /** Change usages. Returns success + optional message */
  changeUsages: (personaId: number, newUsages: PersonaUsage[]) => { success: boolean; message?: string };
  /** Set explicit roleId (nullable) for a scenario persona */
  setRoleId: (personaId: number, roleId: number | null) => void;
}

/**
 * Shared hook that centralizes persona array mutations and business rules.
 * It intentionally does NOT touch legacy fields like defaultPersonaId or ScenarioContextPersona.
 */
export function useScenarioPersonas(
  scenarioPersonas: ScenarioPersonaDto[],
  setValue: UseFormSetValue<ScenarioSettingsType>,
  trigger: UseFormTrigger<ScenarioSettingsType>,
  translate: (key: string, params?: any) => string
): UseScenarioPersonasResult {
  const { validateUsageChange } = usePersonaUsageValidation(scenarioPersonas, translate);

  const defaultPersona = useMemo(
    () => scenarioPersonas.find(p => p.personaUsages?.includes(PersonaUsageEnum.Default)),
    [scenarioPersonas]
  );

  const introPersona = useMemo(
    () => scenarioPersonas.find(p => p.personaUsages?.includes(PersonaUsageEnum.Intro)),
    [scenarioPersonas]
  );

  const hasDefaultPersonaError = useMemo(() => {
    const defaultPersonas = scenarioPersonas.filter(p =>
      p.personaUsages?.includes(PersonaUsageEnum.Default)
    );
    return defaultPersonas.length !== 1;
  }, [scenarioPersonas]);

  const commit = useCallback(async (updated: ScenarioPersonaDto[]) => {
    setValue('scenarioPersonas', updated as any, { shouldDirty: true });
    // Explicitly trigger re-validation to ensure validation state is updated
    await trigger('scenarioPersonas');
  }, [setValue, trigger]);

  const setDefaultPersona = useCallback((personaId: number | null) => {
    // Clear any existing default
    let updated = scenarioPersonas.map(sp => ({
      ...sp,
      personaUsages: sp.personaUsages?.filter(usage => usage !== PersonaUsageEnum.Default) || []
    })) as ScenarioPersonaDto[];

    // Optionally set new default
    if (personaId != null) {
      updated = updated.map(sp => 
        sp.personaId === personaId 
          ? { ...sp, personaUsages: [...(sp.personaUsages || []), PersonaUsageEnum.Default] } 
          : sp
      ) as ScenarioPersonaDto[];
    }

    commit(updated);
  }, [scenarioPersonas, commit]);

  const setIntroPersona = useCallback((personaId: number | null) => {
    // Clear any existing intro
    let updated = scenarioPersonas.map(sp => ({
      ...sp,
      personaUsages: sp.personaUsages?.filter(usage => usage !== PersonaUsageEnum.Intro) || []
    })) as ScenarioPersonaDto[];

    // Optionally set new intro
    if (personaId != null) {
      updated = updated.map(sp => 
        sp.personaId === personaId 
          ? { ...sp, personaUsages: [...(sp.personaUsages || []), PersonaUsageEnum.Intro] } 
          : sp
      ) as ScenarioPersonaDto[];
    }

    commit(updated);
  }, [scenarioPersonas, commit]);

  const addPersona = useCallback((persona: PersonaDto, usages: PersonaUsage[], defaultRoleId?: number | null) => {
    if (!persona?.id) {
      return { success: false, message: 'Persona must have a valid ID' };
    }
    if (!persona.name || persona.name.trim() === '') {
      return { success: false, message: 'Persona must have a non-empty name' };
    }
    if (scenarioPersonas.some(sp => sp.personaId === persona.id)) {
      return { success: false, message: 'Persona is already linked to this scenario' };
    }

    // Validate usages
    const validationResult = validateUsageChange(persona.id, usages);
    if (!validationResult.isValid) {
      return { success: false, message: validationResult.message };
    }

    // Clear existing special usages from other personas
    let updated = [...scenarioPersonas];
    const specialUsages = [PersonaUsageEnum.Default, PersonaUsageEnum.Intro, PersonaUsageEnum.Fallback];
    
    for (const specialUsage of specialUsages) {
      if (usages.includes(specialUsage)) {
        updated = updated.map(sp => ({
          ...sp,
          personaUsages: sp.personaUsages?.filter(usage => usage !== specialUsage) || []
        })) as ScenarioPersonaDto[];
      }
    }

    const newScenarioPersona: ScenarioPersonaDto = {
      personaId: persona.id,
      personaUsages: usages,
      persona: persona,
      roleId: defaultRoleId ?? null
    };

    updated.push(newScenarioPersona);
    commit(updated);
    return { success: true };
  }, [scenarioPersonas, commit, validateUsageChange]);

  const removePersona = useCallback((personaId: number) => {
    const updated = scenarioPersonas.filter(sp => sp.personaId !== personaId);
    commit(updated);
  }, [scenarioPersonas, commit]);

  const changeUsages = useCallback((personaId: number, newUsages: PersonaUsage[]) => {
    const validationResult = validateUsageChange(personaId, newUsages);
    if (!validationResult.isValid) {
      return { success: false, message: validationResult.message };
    }

    let updated = [...scenarioPersonas];
    const specialUsages = [PersonaUsageEnum.Default, PersonaUsageEnum.Intro, PersonaUsageEnum.Fallback];

    // Clear existing special usages from other personas for any special usage being assigned
    for (const specialUsage of specialUsages) {
      if (newUsages.includes(specialUsage)) {
        updated = updated.map(sp => 
          sp.personaId !== personaId 
            ? { ...sp, personaUsages: sp.personaUsages?.filter(usage => usage !== specialUsage) || [] }
            : sp
        ) as ScenarioPersonaDto[];
      }
    }

    // Update the target persona with new usages
    updated = updated.map(sp =>
      sp.personaId === personaId ? { ...sp, personaUsages: newUsages } : sp
    ) as ScenarioPersonaDto[];

    commit(updated);
    return { success: true };
  }, [scenarioPersonas, commit, validateUsageChange]);

  const setRoleId = useCallback((personaId: number, roleId: number | null) => {
    const updated = scenarioPersonas.map(sp =>
      sp.personaId === personaId ? { ...sp, roleId } : sp
    ) as ScenarioPersonaDto[];
    commit(updated);
  }, [scenarioPersonas, commit]);

  return { defaultPersona, introPersona, hasDefaultPersonaError, setDefaultPersona, setIntroPersona, addPersona, removePersona, changeUsages, setRoleId };
}
